import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Store, MapPin, Globe, Tag, ArrowUpRight, Sparkles } from "lucide-react";

/** Encart « Partenaires à l'honneur » : jusqu'à 5 partenaires RÉELS tirés au hasard,
 *  avec liens vers leur mini-site PROXIreduc, leurs offres et leur site web. */
export default function FeaturedPartners({ limit = 5, testId = "featured-partners", track = false, channel = "press", city = "" }) {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let alive = true;
    // Priorise les partenaires de la commune du contenu (annonce / voisin / actu)
    const url = city
      ? `/featured-partners?limit=${limit}&city=${encodeURIComponent(city)}`
      : `/featured-partners?limit=${limit}`;
    api.get(url)
      .then((r) => {
        if (!alive) return;
        const list = r.data?.partners || [];
        setPartners(list);
        if (track && list.length) {
          api.post("/track/editorial-placement", { business_ids: list.map((p) => p.id), channel }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [limit, track, channel, city]);

  if (!partners.length) return null;

  return (
    <section className="mt-12 pt-8 border-t border-slate-100" data-testid={testId}>
      <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
        <Sparkles size={18} className="text-[#E05638]" /> Nos partenaires à l'honneur
      </h2>
      <p className="mt-1 text-sm text-slate-500">Des professionnels locaux à soutenir près de chez vous.</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {partners.map((p) => {
          const siteUrl = p.website
            ? (p.website.startsWith("http") ? p.website : `https://${p.website}`)
            : null;
          return (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3.5 hover:border-[#E05638] transition-colors"
                 data-testid={`featured-partner-${p.id}`}>
              <div className="flex items-center gap-3">
                <Link to={`/commerce/${p.id}`} className="shrink-0" aria-label={p.name}>
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 grid place-items-center">
                    {p.logo || p.cover_image
                      ? <img src={p.logo || p.cover_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      : <Store size={20} className="text-slate-300" />}
                  </div>
                </Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/commerce/${p.id}`} className="font-semibold text-slate-900 text-sm line-clamp-1 hover:text-[#E05638] transition-colors"
                        data-testid={`featured-partner-name-${p.id}`}>{p.name}</Link>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                    {p.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.city}</span>}
                    {p.category && <span className="truncate">· {p.category}</span>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Link to={`/commerce/${p.id}`} data-testid={`featured-partner-minisite-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-[#E05638] border border-orange-200 px-2.5 py-1 text-xs font-semibold hover:bg-orange-100 transition-colors">
                  <Store size={12} /> Mini-site
                </Link>
                {p.offer_count > 0 && (
                  <Link to={p.offer_id ? `/offre/${p.offer_id}` : `/commerce/${p.id}`}
                        data-testid={`featured-partner-offers-${p.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-xs font-semibold hover:bg-emerald-100 transition-colors">
                    <Tag size={12} /> {p.offer_count > 1 ? `${p.offer_count} offres` : "Son offre"}
                  </Link>
                )}
                {siteUrl && (
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer"
                     data-testid={`featured-partner-website-${p.id}`}
                     className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-1 text-xs font-semibold hover:bg-slate-100 transition-colors">
                    <Globe size={12} /> Site web <ArrowUpRight size={11} className="opacity-60" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
